import React, { useState } from "react";
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PublicIcon from "@mui/icons-material/Public";
import RadarIcon from "@mui/icons-material/Radar";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import UndoIcon from "@mui/icons-material/Undo";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { VoicesTopicOrigin, VoicesTopicStatus } from "../../../Enums/voices";
import { VoicesTopic, VoicesTopicFilter } from "../../../Models/voicesTopics";
import {
  useSetVoicesTopicStatus,
  useVoicesRadar,
  useVoicesTopics,
} from "../../../Query/useVoicesTopics";
import { ComoButton } from "../../Common/ComoButton";
import { EmptyState } from "../../HomePage/DashboardCard";
import { VoicesSegmented, voicesToolbarSx } from "../Common/VoicesFilterParts";
import VoicesPostDialog from "../Posts/VoicesPostDialog";
import VoicesTopicChips, { ORIGIN_KEYS } from "./VoicesTopicChips";
import VoicesTopicDialog from "./VoicesTopicDialog";

interface Props {
  campaignId: number;
  canManage: boolean;
  canWritePosts: boolean;
}

const PAGE_SIZE = 20;

export const TOPIC_COLORS: Record<VoicesTopicStatus, string> = {
  [VoicesTopicStatus.NEW]: color.orange600,
  [VoicesTopicStatus.USED]: color.green700,
  [VoicesTopicStatus.DISMISSED]: color.gray500,
};

const ORIGIN_ICONS: Record<VoicesTopicOrigin, React.ReactNode> = {
  [VoicesTopicOrigin.RSS]: <RssFeedIcon sx={{ fontSize: 18 }} />,
  [VoicesTopicOrigin.WEB]: <PublicIcon sx={{ fontSize: 18 }} />,
  [VoicesTopicOrigin.MANUAL]: <EditOutlinedIcon sx={{ fontSize: 18 }} />,
  [VoicesTopicOrigin.AGENT]: <TravelExploreIcon sx={{ fontSize: 18 }} />,
};

const FILTER_STATUS: Record<Exclude<VoicesTopicFilter, "all">, VoicesTopicStatus> = {
  new: VoicesTopicStatus.NEW,
  used: VoicesTopicStatus.USED,
  dismissed: VoicesTopicStatus.DISMISSED,
};

const VoicesTopicList: React.FC<Props> = ({ campaignId, canManage, canWritePosts }) => {
  const { t } = useTranslation("voicesTopics");
  const { colors: workspace } = useWorkspaceColors();
  const [filter, setFilter] = useState<VoicesTopicFilter>("new");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const { data, isLoading, isFetching } = useVoicesTopics(campaignId, filter, limit);
  const radar = useVoicesRadar(campaignId, canManage);
  const setStatus = useSetVoicesTopicStatus(campaignId);

  const [openedTopic, setOpenedTopic] = useState<VoicesTopic | null>(null);
  const [creating, setCreating] = useState(false);
  const [postTopic, setPostTopic] = useState<VoicesTopic | null>(null);

  const shown = data?.topics ?? [];
  const counts = data?.counts ?? { new: 0, used: 0, dismissed: 0, all: 0 };
  const remaining = counts[filter] - shown.length;

  const filters: VoicesTopicFilter[] =
    canManage || counts.dismissed > 0
      ? ["new", "used", "dismissed", "all"]
      : ["new", "used", "all"];

  const openTopic = openedTopic
    ? shown.find((topic) => topic.id === openedTopic.id) ?? openedTopic
    : null;

  const chooseFilter = (value: VoicesTopicFilter) => {
    setFilter(value);
    setLimit(PAGE_SIZE);
  };

  const quickAction = (label: string, icon: React.ReactNode, onClick: () => void) => (
    <Tooltip title={label}>
      <IconButton
        size="small"
        disabled={setStatus.isPending}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        sx={{ color: color.gray400, "&:hover": { color: workspace.accent } }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  const renderRow = (topic: VoicesTopic) => {
    const firstSource = topic.sources[0];
    const tone = TOPIC_COLORS[topic.status];
    const meta = [
      firstSource &&
        `${firstSource.name}${topic.sources.length > 1 ? ` +${topic.sources.length - 1}` : ""}`,
      firstSource?.date && new Date(`${firstSource.date}T00:00:00`).toLocaleDateString(),
      topic.summary,
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <Box component="li" key={topic.id}>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setOpenedTopic(topic)}
          onKeyDown={(event) => event.key === "Enter" && setOpenedTopic(topic)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1,
            py: 1,
            borderRadius: "10px",
            cursor: "pointer",
            opacity: topic.status === VoicesTopicStatus.DISMISSED ? 0.65 : 1,
            "& .topic-actions": { opacity: { xs: 1, md: 0 }, transition: "opacity 0.15s" },
            "&:hover": { backgroundColor: alpha(workspace.accent, 0.08) },
            "&:hover .topic-actions, &:focus-within .topic-actions": { opacity: 1 },
            "&:focus-visible": {
              outline: `2px solid ${workspace.primary_dark}`,
              outlineOffset: -2,
            },
          }}
        >
          <Tooltip title={t(ORIGIN_KEYS[topic.origin])}>
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                color: tone,
                backgroundColor: alpha(tone, 0.12),
              }}
            >
              {ORIGIN_ICONS[topic.origin]}
            </Box>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontSize: 14, lineHeight: "20px", fontWeight: 600, color: color.gray900 }}
            >
              {topic.title}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, lineHeight: "16px", color: color.gray500 }}>
              {meta}
            </Typography>
          </Box>
          {topic.posts.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 600,
                color: color.gray500,
              }}
            >
              <ArticleOutlinedIcon sx={{ fontSize: 15 }} />
              {t("postCount", { count: topic.posts.length })}
            </Box>
          )}
          <VoicesTopicChips topic={topic} showStatus={filter === "all"} />
          <Box className="topic-actions" sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
            {canWritePosts &&
              topic.status !== VoicesTopicStatus.DISMISSED &&
              quickAction(t("createPost"), <EditNoteIcon fontSize="small" />, () =>
                setPostTopic(topic),
              )}
            {canManage &&
              (topic.status === VoicesTopicStatus.DISMISSED
                ? quickAction(t("restore"), <UndoIcon fontSize="small" />, () =>
                    setStatus.mutate({ topicId: topic.id, status: VoicesTopicStatus.NEW }),
                  )
                : quickAction(t("dismiss"), <CloseIcon fontSize="small" />, () =>
                    setStatus.mutate({ topicId: topic.id, status: VoicesTopicStatus.DISMISSED }),
                  ))}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box sx={voicesToolbarSx}>
        <VoicesSegmented
          value={filter}
          onChange={chooseFilter}
          segments={filters.map((item) => ({
            value: item,
            label: t(`filter_${item}`),
            count: counts[item],
            tone: item === "all" ? undefined : TOPIC_COLORS[FILTER_STATUS[item]],
          }))}
        />
        {canManage && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              startIcon={
                radar.running ? (
                  <CircularProgress size={16} sx={{ color: workspace.accent }} />
                ) : (
                  <RadarIcon />
                )
              }
              disabled={radar.running || radar.start.isPending}
              onClick={() => radar.start.mutate()}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: color.gray700,
                "&.Mui-disabled": { color: color.gray500 },
              }}
            >
              {radar.running ? t("radarRunning") : t("runRadar")}
            </Button>
            <ComoButton startIcon={<AddIcon />} onClick={() => setCreating(true)}>
              {t("newTopic")}
            </ComoButton>
          </Box>
        )}
      </Box>

      {!isLoading && !shown.length ? (
        <EmptyState>{counts.all ? t("noTopicsInFilter") : t("noTopics")}</EmptyState>
      ) : (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {shown.map(renderRow)}
        </Box>
      )}
      {remaining > 0 && (
        <Button
          disabled={isFetching}
          startIcon={
            isFetching ? <CircularProgress size={14} sx={{ color: workspace.accent }} /> : null
          }
          onClick={() => setLimit(limit + PAGE_SIZE)}
          sx={{ mt: 0.5, textTransform: "none", fontSize: 13, color: workspace.accent }}
        >
          {t("showMore", { count: Math.min(remaining, PAGE_SIZE), total: remaining })}
        </Button>
      )}

      <VoicesTopicDialog
        open={creating || !!openTopic}
        campaignId={campaignId}
        topic={creating ? null : openTopic}
        canManage={canManage}
        onCreatePost={
          canWritePosts
            ? (topic) => {
                setOpenedTopic(null);
                setPostTopic(topic);
              }
            : undefined
        }
        onClose={() => {
          setCreating(false);
          setOpenedTopic(null);
        }}
      />
      <VoicesPostDialog
        open={!!postTopic}
        campaignId={campaignId}
        post={null}
        topic={postTopic ? { id: postTopic.id, title: postTopic.title } : null}
        onClose={() => setPostTopic(null)}
      />
    </>
  );
};

export default VoicesTopicList;
